import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AuthUserState, GeneralUserService } from '@coin/shared/auth/data-access';
import { PermissionResource } from '@coin/shared/core/util-enums';
import { CoinUser, Employee, MergedPermission, UserAuthStore } from '@coin/shared/core/util-models';
import { defer, Observable, of, shareReplay } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, startWith, switchMap } from 'rxjs/operators';

export interface UserStateModel {
  user: CoinUser; // can be the emulated user - otherwise identical with loggedInUser
  loggedInUser: CoinUser; // static - always the real user
  token: string;
  directs: Employee[];
  isDynamicMercerAccessAllowed: boolean;
  accessViews: PermissionResource[]; // always based on user (not loggedInUser)
}

// Auto-migrated from NGXS
@Injectable({ providedIn: 'root' })
export class UserState implements AuthUserState {
  private readonly userService = inject(GeneralUserService);

  private readonly userStateModel = signal<UserStateModel>({
    token: '',
    user: null,
    loggedInUser: null,
    directs: [],
    isDynamicMercerAccessAllowed: false,
    accessViews: undefined
  });

  public user = computed(() => this.userStateModel().user);

  public accessViews = computed(() =>
    this.userStateModel().accessViews?.reduce<Set<PermissionResource>>((set, resource) => {
      set.add(resource);
      return set;
    }, new Set())
  );

  public allPermissions = computed(() => {
    const allRolePermissions = this.userStateModel().user?.roles?.flatMap(role => role.permissions);
    return allRolePermissions?.reduce<Map<PermissionResource, MergedPermission>>((permissionMap, permission) => {
      if (!permissionMap.has(permission.resource)) {
        permissionMap.set(permission.resource, {
          resource: permission.resource,
          httpActions: new Set([permission.httpAction])
        });
      } else {
        permissionMap.get(permission.resource).httpActions.add(permission.httpAction);
      }
      return permissionMap;
    }, new Map());
  });

  public isDynamicMercerAccessAllowed = computed(() => this.userStateModel().isDynamicMercerAccessAllowed);

  private readonly userChange$ = toObservable(this.user);
  private readonly accessViewsChange$ = toObservable(this.accessViews);
  private readonly allPermissionsChange$ = toObservable(this.allPermissions);
  private readonly isDynamicMercerAccessAllowedChange$ = toObservable(this.isDynamicMercerAccessAllowed);

  // ngxs compatibility: like store.select, these emit the current value on subscribe and every change afterwards
  public readonly user$ = this.select(this.user, this.userChange$);
  public readonly accessViews$ = this.select(this.accessViews, this.accessViewsChange$);
  public readonly allPermissions$ = this.select(this.allPermissions, this.allPermissionsChange$);
  public readonly isDynamicMercerAccessAllowed$ = this.select(
    this.isDynamicMercerAccessAllowed,
    this.isDynamicMercerAccessAllowedChange$
  );

  // Keep lazy and spot-load via toSignal() in consumers; an eager subscriber fires this in Cosmos too (GMTEC-21089)
  public currentUserDetails$ = this.user$.pipe(
    filter(user => !!user),
    switchMap(user => this.userService.getEmployeeDetailsByGid(user.gid).pipe(catchError(() => of(null)))),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  loadUser(useAsLoggedInUser = false): Observable<void> {
    if (this.userStateModel().user?.gid) {
      return of(null);
    }

    return this.userService.getUserByToken().pipe(
      switchMap(user => {
        this.loadUserSuccess(user, useAsLoggedInUser);
        return of(undefined);
      })
    );
  }

  loadLoggedInUser(): Observable<void> {
    if (this.userStateModel().loggedInUser?.gid) {
      return of(null);
    }

    return this.userService.getUserByToken(true).pipe(
      switchMap(user => {
        this.loadLoggedInUserSuccess(user);
        return of(undefined);
      })
    );
  }

  private loadUserSuccess(payload: UserAuthStore, useAsLoggedInUser = false): void {
    this.userStateModel.update(state => {
      const update = {
        ...state,
        token: payload.token,
        user: payload.principal,
        accessViews: payload.accessViews
      };

      if (useAsLoggedInUser) {
        update.loggedInUser = payload.principal;
      }

      return update;
    });
  }

  private loadLoggedInUserSuccess(payload: UserAuthStore): void {
    this.userStateModel.update(state => ({ ...state, loggedInUser: payload.principal }));
  }

  setDynamicMercerAccess(hasAccess: boolean): void {
    this.userStateModel.update(state => ({ ...state, isDynamicMercerAccessAllowed: hasAccess }));
  }

  resetUser(): void {
    this.userStateModel.set({
      token: '',
      user: null,
      loggedInUser: null,
      directs: [],
      isDynamicMercerAccessAllowed: false,
      accessViews: []
    });
  }

  private select<T>(value: Signal<T>, change$: Observable<T>): Observable<T> {
    return defer(() =>
      change$.pipe(
        map(() => value()),
        startWith(value()),
        distinctUntilChanged()
      )
    );
  }
}
